import type { CoverageRequirement, WizardBand, WizardDraft, WizardLane } from '../../services/schedulerWizardApi';

export const WIZARD_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const WIZARD_PRESETS = ['24/7', '7-day day/night', '5/2 weekend', 'Weekday night + whole weekend', '7-day regional', 'Custom'] as const;
export type WizardPreset = typeof WIZARD_PRESETS[number];
export function wizardBand(day: string, startTime: string, endTime: string, offset = 0): WizardBand {
  return { id: crypto.randomUUID(), startDay: day, startTime, endTime, endDayOffset: offset,
    endDay: WIZARD_DAYS[(WIZARD_DAYS.indexOf(day) + offset) % 7] };
}
export function wizardLane(groupId: string, bands: WizardBand[], role: WizardLane['role'] = 'Primary'): WizardLane {
  return { id: crypto.randomUUID(), role, groupId, bands, assignmentCadence: 'Cycle', rosterUserIds: [], initialUserId: '' };
}
export function wizardRequirements(bands: WizardBand[], role: WizardLane['role'] = 'Primary'): CoverageRequirement[] {
  return bands.map(band => ({ ...band, id: crypto.randomUUID(), role, required: true, requiredPositions: 1, allowParallel: false }));
}
export function applyWizardPreset(draft: WizardDraft, preset: WizardPreset, groupId: string): WizardDraft {
  const days = (count: number, start: string, end: string, offset = 0) =>
    WIZARD_DAYS.slice(0, count).map(day => wizardBand(day, start, end, offset));
  let bands: WizardBand[][];
  if (preset === '24/7') bands = [[wizardBand('Monday', '00:00', '00:00', 7)]];
  else if (preset === '7-day day/night') bands = [days(7, '08:00', '18:00'), days(7, '18:00', '08:00', 1)];
  else if (preset === '5/2 weekend' || preset === 'Weekday night + whole weekend') {
    const nights = days(4, '18:00', '08:00', 1);
    const weekend = wizardBand('Friday', '18:00', '08:00', 3);
    bands = preset === '5/2 weekend' ? [days(5, '08:00', '18:00'), nights, [weekend]]
      : [days(5, '08:00', '18:00'), [...nights, weekend]];
  } else if (preset === '7-day regional') {
    bands = [days(7, '00:00', '08:00'), days(7, '08:00', '16:00'), days(7, '16:00', '00:00', 1)];
  } else bands = [[wizardBand('Monday', '09:00', '17:00')]];
  const primary = bands.map(items => wizardLane(groupId, items));
  const requirements = preset === 'Custom' ? wizardRequirements(primary.flatMap(lane => lane.bands))
    : wizardRequirements([wizardBand('Monday', '00:00', '00:00', 7)]);
  return { ...draft, lanes: primary, coverageRequirements: requirements, ownershipAssociations: [], exclusions: [],
    decisions: { escalation: 'NotConfigured', additionalRequirements: 'NotConfigured' } };
}
export function newWizardDraft(): WizardDraft {
  const today = new Date();
  const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + ((8 - today.getDay()) % 7));
  const date = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
  return {
    draftId: crypto.randomUUID(), name: '', description: '', timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
    effectiveStartLocal: { date, time: '09:00' }, effectiveEndLocal: null,
    cycle: { basis: 'CalendarWeeks', weeks: 1, anchorWeekday: 'Monday', anchorTime: '09:00',
      phaseDate: date, invalidLocalTimeResolution: 'Reject' },
    lanes: [], coverageRequirements: [], ownershipAssociations: [], exclusions: [],
    decisions: { escalation: 'NotConfigured', additionalRequirements: 'NotConfigured' },
  };
}

export function wizardDraftWithStartDate(draft: WizardDraft, date: string): WizardDraft {
  let phaseDate = draft.cycle.phaseDate;
  if (date && phaseDate > date) {
    const start = new Date(`${date}T12:00:00Z`);
    const anchorIndex = WIZARD_DAYS.indexOf(draft.cycle.anchorWeekday);
    if (!Number.isFinite(start.getTime()) || anchorIndex < 0) {
      throw new Error('Choose a valid effective start date and cycle anchor weekday.');
    }
    const anchorDay = (anchorIndex + 1) % 7;
    start.setUTCDate(start.getUTCDate() - (start.getUTCDay() - anchorDay + 7) % 7);
    phaseDate = start.toISOString().slice(0, 10);
  }
  return { ...draft, effectiveStartLocal: { ...draft.effectiveStartLocal, date },
    cycle: { ...draft.cycle, phaseDate } };
}
